/**
 * Organization Settings - General Section
 * Allows editing organization name, logo, and viewing org details
 */

import React, { useState, useEffect, useRef } from 'react';
import { Building2, Users, Armchair, Save, X, Upload, Trash2, ImageIcon } from 'lucide-react';
import { useOrganization } from '@clerk/clerk-react';
import { useAuth } from '../../../contexts/AuthContext';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export function OrganizationSettingsGeneral() {
    const { organization, isLoaded } = useOrganization();
    const { currentOrg, canManageOrg } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [name, setName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    useEffect(() => {
        if (organization) {
            setName(organization.name);
        }
    }, [organization]);

    useEffect(() => {
        if (organization) {
            setHasChanges(name !== organization.name);
        }
    }, [name, organization]);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
    };

    const handleSave = async () => {
        if (!organization || !name.trim()) return;

        setIsSaving(true);
        try {
            await organization.update({ name: name.trim() });
            showToast('success', 'Organization name has been updated.');
            setHasChanges(false);
        } catch (error) {
            showToast('error', error instanceof Error ? error.message : 'Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !organization) return;

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            showToast('error', 'Image size must be under 2MB');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('error', 'Please upload an image file');
            return;
        }

        setIsUploadingImage(true);
        try {
            await organization.setLogo({ file });
            showToast('success', 'Organization logo updated successfully');
        } catch (error) {
            showToast('error', error instanceof Error ? error.message : 'Failed to upload logo');
        } finally {
            setIsUploadingImage(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveImage = async () => {
        if (!organization) return;

        setIsUploadingImage(true);
        try {
            await organization.setLogo({ file: null });
            showToast('success', 'Organization logo removed');
        } catch (error) {
            showToast('error', error instanceof Error ? error.message : 'Failed to remove logo');
        } finally {
            setIsUploadingImage(false);
        }
    };

    if (!isLoaded) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
                <div className="h-48 bg-slate-200 rounded-xl animate-pulse" />
                <div className="h-36 bg-slate-200 rounded-xl animate-pulse" />
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <p className="text-slate-500">No organization found.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Toast Notification */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
                    toast.type === 'success' 
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                    <p className="text-sm font-medium">{toast.message}</p>
                </div>
            )}

            {/* Header */}
            <div>
                <h2 className="text-xl font-semibold text-slate-900">General Settings</h2>
                <p className="text-slate-500 text-sm mt-1">
                    Manage your organization's basic information
                </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                        <Users className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium">Team Members</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{currentOrg?.membersCount || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Active members</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                        <Armchair className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Available Seats</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                        {(currentOrg?.maxMembers || 5) - (currentOrg?.membersCount || 0)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">of {currentOrg?.maxMembers || 5} total</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        <span className="text-sm font-medium">Organization</span>
                    </div>
                    <p className="text-lg font-bold text-slate-900 truncate">{organization.name}</p>
                    <p className="text-xs text-slate-500 mt-1 truncate">{organization.slug || 'No slug'}</p>
                </div>
            </div>

            {/* Organization Logo */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-base font-semibold text-slate-900">Organization Logo</h3>
                </div>
                <div className="p-6">
                    <div className="flex items-start space-x-6">
                        {/* Logo Preview */}
                        <div className="flex-shrink-0">
                            {organization.imageUrl ? (
                                <img 
                                    src={organization.imageUrl} 
                                    alt={organization.name}
                                    className="w-24 h-24 rounded-xl object-cover border border-slate-200"
                                />
                            ) : (
                                <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center border border-slate-200">
                                    <Building2 className="w-10 h-10 text-slate-400" />
                                </div>
                            )}
                        </div>

                        {/* Upload Controls */}
                        <div className="flex-1">
                            <p className="text-sm text-slate-600 mb-3">
                                Upload a logo for your organization. This will be displayed across the platform.
                            </p>
                            <p className="text-xs text-slate-500 mb-4 flex items-center space-x-1">
                                <ImageIcon className="w-3.5 h-3.5" />
                                <span>Recommended size: 1:1 aspect ratio, up to 2MB</span>
                            </p>

                            {canManageOrg ? (
                                <div className="flex items-center space-x-3">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        id="org-logo-upload"
                                        disabled={isUploadingImage}
                                    />
                                    <label
                                        htmlFor="org-logo-upload"
                                        className={`inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                                            isUploadingImage 
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                                                : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        }`}
                                    >
                                        {isUploadingImage ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                                <span>Uploading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                <span>Upload Logo</span>
                                            </>
                                        )}
                                    </label>

                                    {organization.hasImage && (
                                        <button
                                            onClick={handleRemoveImage}
                                            disabled={isUploadingImage}
                                            className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            <span>Remove</span>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <p className="text-xs text-slate-500">
                                    Only admins can change the organization logo
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Organization Details */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-base font-semibold text-slate-900">Organization Details</h3>
                </div>
                <div className="p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Organization Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter organization name"
                            disabled={!canManageOrg}
                            className={`w-full px-4 py-2.5 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${
                                !canManageOrg ? 'bg-slate-50 cursor-not-allowed' : 'bg-white'
                            }`}
                        />
                        {!canManageOrg && (
                            <p className="text-xs text-slate-500 mt-1.5">
                                Only admins can change the organization name
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Organization ID
                        </label>
                        <input
                            type="text"
                            value={organization.id}
                            readOnly
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-mono text-sm cursor-not-allowed"
                        />
                        <p className="text-xs text-slate-500 mt-1.5">
                            Unique identifier for your organization
                        </p>
                    </div>

                    {organization.slug && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Organization Slug
                            </label>
                            <input
                                type="text"
                                value={organization.slug}
                                readOnly
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 cursor-not-allowed"
                            />
                        </div>
                    )}

                    {canManageOrg && hasChanges && (
                        <div className="flex justify-end space-x-3 pt-2">
                            <button
                                onClick={() => setName(organization.name)}
                                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex items-center space-x-2"
                            >
                                <X className="w-4 h-4" />
                                <span>Cancel</span>
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSaving ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span>Save Changes</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OrganizationSettingsGeneral;
